import React, { useState } from 'react'
import { ArrowCircleDown } from '../../../common/components/Icons/ArrowCircleDown'
import { ArrowCircleUp } from '../../../common/components/Icons/ArrowCircleUp'

export interface ExampleTab {
  id: number
  title: string
  content: string
}

interface ExampleTabMenuProps {
  className?: string
  tabs: ExampleTab[]
  isLoading: boolean
}

const Tabs = ({
  handleTabSelection,
  tabs,
  activeTab,
  displayClassName,
}: {
  handleTabSelection: (tab: number) => void
  tabs: { id: number }[]
  activeTab: number
  displayClassName: string
}) => (
  <div className={`${displayClassName} justify-center gap-4 sm:gap-6 md:gap-8`}>
    {tabs.map((tab, key) => (
      <button
        key={tab.id}
        onClick={() => handleTabSelection(key)}
        className={`text-sm font-medium rounded-full w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 ${
          tabs[activeTab].id === tab.id ? 'bg-[#A4E0D8]' : 'bg-[#D9E0DF]'
        }`}
      >
        {tab.id}
      </button>
    ))}
  </div>
)

export const ExampleTabMenu = ({
  className,
  tabs,
  isLoading,
}: ExampleTabMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  const toggleMenu = () => setIsOpen(!isOpen)
  const handleTabSelection = (tab: number) => setActiveTab(tab)

  return (
    <div
      className={` ${
        isOpen ? 'sm:w-full absolute top-0' : 'sm:w-auto relative'
      } sm:static w-auto m-4 sm:m-0 bg-white rounded-lg sm:rounded-l-lg sm:rounded-r-none shadow-md sm:shadow-lg outline outline-[0.2px] sm:outline-[0.6px] sm:outline-offset-[-0.6px] outline-zinc-200 flex flex-col sm:max-w-[420px] p-4 sm:p-6 z-10 ${className}`}
    >
      <div className='flex flex-col gap-4'>
        {isLoading ? (
          'Loading...'
        ) : (
          <>
            <button
              className='flex items-center justify-between gap-2 w-full sm:w-auto text-xs sm:text-sm leading-none sm:leading-tight tracking-wide text-slate-900 font-medium font-["DM_Sans"]'
              onClick={toggleMenu}
            >
              <span>View search examples</span>
              {isOpen ? (
                <ArrowCircleUp className='w-4 h-4 md:w-6 md:h-6 relative top-[1px]' />
              ) : (
                <ArrowCircleDown className='w-4 h-4 md:w-6 md:h-6 relative top-[1px]' />
              )}
            </button>

            {isOpen && (
              <div className='flex flex-col w-full gap-4 pt-2'>
                <hr className='h-[2px] bg-black opacity-25 hidden sm:block' />
                <div className='contents sm:hidden'>
                  <div className='self-stretch h-px opacity-25 bg-[radial-gradient(ellipse_50.00%_50.00%_at_50.00%_50.00%,_#0C3A78_0%,_rgba(24.63,_64.96,_118.73,_0)_100%)]' />
                  <div className='self-stretch h-px opacity-25 bg-[radial-gradient(ellipse_50.00%_50.00%_at_50.00%_50.00%,_#0C3A78_0%,_rgba(24.63,_64.96,_118.73,_0)_100%)]' />
                </div>
                <Tabs
                  {...{ handleTabSelection, activeTab, tabs }}
                  displayClassName='sm:hidden flex'
                />

                <div className='flex justify-start'>
                  <div className='flex justify-center items-center min-h-6'>
                    <span className='font-medium px-6 py-[6px] bg-[#D2F6FF] rounded-lg text-center text-xs sm:text-sm md:text-base'>
                      {tabs[activeTab].title}
                    </span>
                  </div>
                </div>

                <span className='text-[#566885] text-justify text-xs sm:text-sm md:text-base leading-relaxed'>
                  {tabs[activeTab].content}
                </span>
                <Tabs
                  {...{ handleTabSelection, activeTab, tabs }}
                  displayClassName='sm:flex hidden'
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
